/**
 * Modification d'une application décrite en langage naturel (§3.5).
 *
 * L'autre voie — recoller un artifact entier — suppose deux choses qui sont
 * rarement vraies : savoir lire le code, et l'avoir encore sous la main. Trois
 * semaines après un déploiement, l'auteur a fermé sa conversation et ne retrouve
 * pas son artifact ; et s'il le retrouve, il ne sait pas y ajouter une colonne.
 *
 * Ici il décrit ce qu'il veut. Le point de départ est le code de la dernière
 * version aboutie, que le serveur va chercher lui-même : rien à retrouver, rien à
 * coller.
 *
 * Deux garanties sont conservées telles quelles, parce qu'une modification en
 * langage naturel n'est pas moins engageante qu'un collage de code : le
 * commentaire de version reste obligatoire, et la confirmation aussi. Et le
 * panneau de validation des changements de comportement s'appliquera de la même
 * façon — c'est même ici qu'il sert le plus, puisque l'auteur ne verra pas passer
 * le code.
 */

import {
  MAX_COMMENT_LENGTH,
  MAX_MODIFICATION_INSTRUCTION_LENGTH,
  MAX_VERSION_COMMENT_LENGTH,
  modifyAppSchema,
  versionCommentSchema,
} from '@portail/shared';
import clsx from 'clsx';
import { Lock, Sparkles } from 'lucide-react';
import { useId, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ApiError, modifyApp } from '../api';
import { messageOf } from '../hooks/useAsync';
import { useSession } from '../hooks/useSession';
import { formatNumber } from '../lib/format';
import { ConfirmationCheckbox } from './ConfirmationCheckbox';
import { Button, ErrorBanner, Label } from './ui';

/** Première erreur d'un champ, ou `null` s'il est valide. */
function firstError(
  schema: { safeParse: (value: unknown) => unknown },
  value: string,
): string | null {
  const result = schema.safeParse(value) as
    { success: true } | { success: false; error: { issues: { message: string }[] } };
  return result.success ? null : (result.error.issues[0]?.message ?? 'Valeur invalide.');
}

/** Schéma du seul champ de demande, extrait pour valider avant l'envoi. */
const instructionSchema = modifyAppSchema.shape.instruction;

/**
 * Exemples proposés, choisis pour montrer l'éventail plutôt que pour être
 * recopiés : une correction, un ajout visuel, un changement de texte.
 */
const EXEMPLES = [
  'Ajoute une colonne « Responsable » au tableau, entre « Client » et « Montant ».',
  'Le bouton « Valider » doit être vert, et désactivé tant que le formulaire est incomplet.',
  'Remplace le titre « Suivi » par « Suivi commercial 2026 ».',
];

export interface ModifyFormProps {
  appId: string;
  slug: string;
  /** Numéro de la version en ligne, pour annoncer celui de la suivante. */
  currentVersionNumber?: number | null;
  /** Commentaire actuel de l'application, proposé comme valeur de départ. */
  currentComment: string | null;
  onCancel?: () => void;
}

/** Demande de modification en français, sans code. */
export function ModifyForm({
  appId,
  slug,
  currentVersionNumber,
  currentComment,
  onCancel,
}: ModifyFormProps) {
  const navigate = useNavigate();
  const { settings } = useSession();
  const instructionId = useId();
  const versionCommentId = useId();
  const commentId = useId();

  const [instruction, setInstruction] = useState('');
  const [instructionTouched, setInstructionTouched] = useState(false);
  const [versionComment, setVersionComment] = useState('');
  const [versionCommentTouched, setVersionCommentTouched] = useState(false);
  const [comment, setComment] = useState(currentComment ?? '');
  const [confirmed, setConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [failure, setFailure] = useState<ApiError | null>(null);

  const instructionError = firstError(instructionSchema, instruction);
  const versionError = firstError(versionCommentSchema, versionComment);

  const missing: string[] = [];
  if (instructionError !== null) missing.push('la description de la modification');
  if (versionError !== null) missing.push('le commentaire de version');
  if (!confirmed) missing.push('la confirmation');

  const canSubmit = missing.length === 0 && !submitting;

  const onSubmit = (): void => {
    setInstructionTouched(true);
    setVersionCommentTouched(true);
    if (!canSubmit) return;
    setSubmitting(true);
    setFailure(null);

    modifyApp(appId, {
      instruction: instruction.trim(),
      versionComment: versionComment.trim(),
      comment: comment.trim() ? comment.trim() : null,
      confirmed: true,
    })
      .then((response) => navigate(`/deploiements/${response.jobId}`))
      .catch((error: unknown) => {
        setFailure(error instanceof ApiError ? error : new ApiError(0, messageOf(error)));
        setSubmitting(false);
      });
  };

  return (
    <form
      className="space-y-5"
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit();
      }}
    >
      <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600 dark:border-slate-800 dark:bg-slate-950 dark:text-slate-400">
        <p className="flex items-start gap-1.5">
          <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          <span>
            Le point de départ est le code de la dernière version en ligne de «&nbsp;{slug}&nbsp;» :
            vous n'avez rien à retrouver ni à recoller.
            {typeof currentVersionNumber === 'number'
              ? ` La v${currentVersionNumber} sera remplacée par la v${currentVersionNumber + 1}, et restera restaurable.`
              : null}
          </span>
        </p>
      </div>

      <div>
        <Label
          htmlFor={instructionId}
          required
          hint={`${formatNumber(instruction.length)} / ${formatNumber(MAX_MODIFICATION_INSTRUCTION_LENGTH)}`}
        >
          Ce que vous voulez changer
        </Label>
        <textarea
          id={instructionId}
          value={instruction}
          rows={5}
          maxLength={MAX_MODIFICATION_INSTRUCTION_LENGTH}
          aria-invalid={instructionTouched && instructionError !== null}
          placeholder="Décrivez la modification en français, comme vous l'expliqueriez à quelqu'un…"
          onChange={(event) => setInstruction(event.target.value)}
          onBlur={() => setInstructionTouched(true)}
          className={clsx(
            'w-full rounded-md border bg-white px-3 py-2 text-sm',
            'text-slate-900 dark:bg-slate-950 dark:text-slate-100',
            instructionTouched && instructionError !== null
              ? 'border-red-400 dark:border-red-800'
              : 'border-slate-300 dark:border-slate-700',
            'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600',
          )}
        />
        {instructionTouched && instructionError !== null ? (
          <p role="alert" className="mt-1 text-sm text-red-600 dark:text-red-400">
            {instructionError}
          </p>
        ) : (
          <div className="mt-2">
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Plus vous êtes précis, plus le résultat vous ressemblera. Par exemple :
            </p>
            <ul className="mt-1 space-y-1">
              {EXEMPLES.map((exemple) => (
                <li key={exemple}>
                  {/* Cliquable : recopier un exemple à la main pour le modifier
                      ensuite est un travail que la machine fait mieux. */}
                  <button
                    type="button"
                    onClick={() => {
                      setInstruction(exemple);
                      setInstructionTouched(false);
                    }}
                    className="text-left text-xs text-indigo-700 underline underline-offset-2 hover:text-indigo-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 dark:text-indigo-300"
                  >
                    {exemple}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <div>
        <Label
          htmlFor={versionCommentId}
          required
          hint={`${formatNumber(versionComment.length)} / ${formatNumber(MAX_VERSION_COMMENT_LENGTH)}`}
        >
          Commentaire de version
        </Label>
        <textarea
          id={versionCommentId}
          value={versionComment}
          rows={2}
          maxLength={MAX_VERSION_COMMENT_LENGTH}
          aria-invalid={versionCommentTouched && versionError !== null}
          placeholder="Ce que change cette version, pour l'historique…"
          onChange={(event) => setVersionComment(event.target.value)}
          onBlur={() => setVersionCommentTouched(true)}
          className={clsx(
            'w-full rounded-md border bg-white px-3 py-2 text-sm',
            'text-slate-900 dark:bg-slate-950 dark:text-slate-100',
            versionCommentTouched && versionError !== null
              ? 'border-red-400 dark:border-red-800'
              : 'border-slate-300 dark:border-slate-700',
            'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600',
          )}
        />
        {versionCommentTouched && versionError !== null ? (
          <p role="alert" className="mt-1 text-sm text-red-600 dark:text-red-400">
            {versionError}
          </p>
        ) : null}
      </div>

      <div>
        <Label
          htmlFor={commentId}
          hint={`${formatNumber(comment.length)} / ${formatNumber(MAX_COMMENT_LENGTH)}`}
        >
          Commentaire de l'application
        </Label>
        <textarea
          id={commentId}
          value={comment}
          rows={2}
          maxLength={MAX_COMMENT_LENGTH}
          onChange={(event) => setComment(event.target.value)}
          className={clsx(
            'w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm',
            'text-slate-900 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100',
            'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600',
          )}
        />
      </div>

      <ConfirmationCheckbox
        checked={confirmed}
        onChange={setConfirmed}
        text={settings.confirmationText}
      />

      {failure ? <ErrorBanner message={failure.message} issues={failure.issues} /> : null}

      <div className="flex flex-wrap items-center gap-3">
        <Button type="submit" variant="primary" disabled={!canSubmit} loading={submitting}>
          <Sparkles className="h-4 w-4" aria-hidden="true" />
          Appliquer la modification
        </Button>
        {onCancel ? (
          <Button variant="secondary" onClick={onCancel} type="button">
            Annuler
          </Button>
        ) : null}
        {missing.length > 0 ? (
          <span className="text-sm text-slate-600 dark:text-slate-400">
            Il manque : {missing.join(', ')}.
          </span>
        ) : null}
      </div>

      <p className="text-xs text-slate-500 dark:text-slate-400">
        Vous verrez le résultat avant qu'il ne remplace l'application : si la modification change
        son fonctionnement, le déploiement s'arrêtera pour vous demander votre accord. Et la version
        actuelle reste restaurable en un clic.
      </p>
    </form>
  );
}
